/**
 * Shared prerequisite gate for the conditions QueryBuilder wrappers
 * (Guardrail / Workflow / Communication / Claim Milestone). The catalog fetch needs a full scope,
 * so the builder stays gated until the author has picked an event, a LOB scope
 * (concrete LOBs or the all-LOBs flag), and — for sub-org-enabled orgs — a
 * sub-org scope (an explicit pick or the all-sub-orgs flag). A rule set that
 * no event dispatches (claim milestones) passes a `null` event and is never
 * asked for one.
 *
 * The placeholder copy names only the dimensions still missing, so it stays true
 * in every combination; the listing order matches the dialogs' vertical layout.
 */

export interface PrerequisiteCheck {
  /** True when event_key (unless null) + LOB scope + (sub-org set OR all-sub-orgs OR sub-orgs-disabled) all hold. */
  prerequisitesMet: boolean;
  /** Copy shown in place of the QB while prerequisites aren't met; `null` when met. */
  prerequisiteMessage: string | null;
}

/**
 * Join phrases into an English list with a trailing "and":
 *  1 → "a"; 2 → "a and b"; 3+ → "a, b, and c" (Oxford comma from three up).
 */
export const joinWithAnd = (parts: string[]): string => {
  if (parts.length <= 1) {
    return parts[0] ?? '';
  }
  const head = parts.slice(0, -1).join(', ');
  const oxfordComma = parts.length > 2 ? ',' : '';
  return `${head}${oxfordComma} and ${parts[parts.length - 1]}`;
};

export const checkConditionPrerequisites = (
  eventKey: string | null,
  isAllLobs: boolean,
  lobs: string[],
  isAllSubOrgs: boolean,
  subOrgIds: number[],
  subOrgsEnabled: boolean
): PrerequisiteCheck => {
  const hasOrgScope = !subOrgsEnabled || isAllSubOrgs || (subOrgIds && subOrgIds.length > 0);
  const missing = [
    ...(eventKey || eventKey === null ? [] : ['an Event']),
    ...(isAllLobs || lobs.length > 0 ? [] : ['a Line of Business']),
    ...(hasOrgScope ? [] : ['a Sub-Organization']),
  ];
  if (missing.length === 0) {
    return { prerequisitesMet: true, prerequisiteMessage: null };
  }
  return {
    prerequisitesMet: false,
    prerequisiteMessage: `Select ${joinWithAnd(missing)} to configure conditions.`,
  };
};
